import React, { useState } from 'react';
import { api } from './App';
import { Wave, Calendar, Books, ArrowRight, ArrowLeft } from './Icons';
import SpreadsheetImport from './SpreadsheetImport';

const PROVIDERS = ['BARBRI', 'BPP', 'Kaplan', 'Revise SQE', 'Epimeleia', 'ULaw', 'Other'];

export default function Onboarding({ userName, onComplete }) {
  const [step, setStep] = useState(1);
  const [name, setName]             = useState(userName || '');
  const [flk1Date, setFlk1Date]     = useState('');
  const [flk2Date, setFlk2Date]     = useState('');
  const [provider, setProvider]     = useState('');
  const [providerOther, setProviderOther] = useState('');
  const [saving, setSaving]         = useState(false);
  const [importCard, setImportCard] = useState(null); // null | 'template' | 'custom' | 'skip'

  // Save steps 1-3 settings then advance to step 4 (import)
  const saveAndAdvance = async (skipProvider) => {
    setSaving(true);
    const effectiveProvider = skipProvider ? '' : (provider === 'Other' ? providerOther : provider);
    const accountUpdate = { name };
    if (flk1Date) accountUpdate.exam_date_flk1 = flk1Date;
    if (flk2Date) accountUpdate.exam_date_flk2 = flk2Date;
    const settingsUpdate = {};
    if (flk1Date) settingsUpdate.flk1_exam_date = flk1Date;
    if (flk2Date) settingsUpdate.flk2_exam_date = flk2Date;
    if (effectiveProvider) settingsUpdate.material_provider = effectiveProvider;
    await Promise.all([
      api('/api/account', { method: 'PATCH', body: JSON.stringify(accountUpdate) }),
      api('/api/settings', { method: 'POST', body: JSON.stringify(settingsUpdate) }),
    ]);
    setSaving(false);
    setStep(4);
  };

  // Complete onboarding — activate tour, enter the app
  const finishOnboarding = async () => {
    await api('/api/settings', { method: 'POST', body: JSON.stringify({
      onboarding_complete: '1',
      tour_shown: '0',
    }) });
    onComplete(name, 'dashboard');
  };

  const dots = (
    <div className="ob-dots">
      {[1, 2, 3, 4].map(n => (
        <span key={n} className={`ob-dot ${step === n ? 'active' : step > n ? 'done' : ''}`} />
      ))}
    </div>
  );

  // ── Step 1: Name ──
  if (step === 1) return (
    <div className="ob-overlay">
      <div className="ob-modal">
        {dots}
        <div className="ob-icon"><Wave size={32} /></div>
        <h2 className="ob-title">Welcome to SQETrack</h2>
        <p className="ob-text">
          Let's take a minute to get you set up. We'll configure your exam dates, study materials, and get your dashboard working.
        </p>
        <label className="ob-label">What should we call you?</label>
        <input className="ob-input" value={name} onChange={e => setName(e.target.value)}
          placeholder="Your first name" autoFocus
          onKeyDown={e => e.key === 'Enter' && name.trim() && setStep(2)} />
        <button className="ob-btn" onClick={() => setStep(2)} disabled={!name.trim()}>
          Continue <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );

  // ── Step 2: Exam dates ──
  if (step === 2) return (
    <div className="ob-overlay">
      <div className="ob-modal">
        {dots}
        <div className="ob-icon"><Calendar size={32} /></div>
        <h2 className="ob-title">When are your exams, {name}?</h2>
        <p className="ob-text">
          Your exam dates power the countdown, urgency ranking, and revision planner. You can update these any time in Settings.
        </p>
        <div className="ob-date-row">
          <div className="ob-date-col">
            <label className="ob-label">FLK1 date</label>
            <input className="ob-input" type="date" value={flk1Date} onChange={e => setFlk1Date(e.target.value)} />
          </div>
          <div className="ob-date-col">
            <label className="ob-label">FLK2 date</label>
            <input className="ob-input" type="date" value={flk2Date} onChange={e => setFlk2Date(e.target.value)} />
          </div>
        </div>
        <div className="ob-btn-row">
          <button className="ob-back" onClick={() => setStep(1)}><ArrowLeft size={14} /> Back</button>
          <button className="ob-btn" onClick={() => setStep(3)}>Continue <ArrowRight size={14} /></button>
        </div>
        <button className="ob-skip" onClick={() => setStep(3)}>Skip for now</button>
      </div>
    </div>
  );

  // ── Step 3: Study materials ──
  if (step === 3) return (
    <div className="ob-overlay">
      <div className="ob-modal">
        {dots}
        <div className="ob-icon"><Books size={32} /></div>
        <h2 className="ob-title">Which study materials are you using?</h2>
        <p className="ob-text">
          This helps us give the most relevant guidance when importing MCQ results.
        </p>
        <div className="ob-provider-grid">
          {PROVIDERS.map(p => (
            <button key={p} className={`ob-provider-btn ${provider === p ? 'selected' : ''}`}
              onClick={() => setProvider(p)}>{p}</button>
          ))}
        </div>
        {provider === 'Other' && (
          <input className="ob-input" value={providerOther} onChange={e => setProviderOther(e.target.value)}
            placeholder="Which provider?" style={{ marginTop: 10 }} autoFocus />
        )}
        <div className="ob-btn-row">
          <button className="ob-back" onClick={() => setStep(2)}><ArrowLeft size={14} /> Back</button>
          <button className="ob-btn" onClick={() => saveAndAdvance(false)} disabled={saving}>
            {saving ? 'Setting up…' : <>Continue <ArrowRight size={14} /></>}
          </button>
        </div>
        <button className="ob-skip" onClick={() => saveAndAdvance(true)}>Skip for now</button>
      </div>
    </div>
  );

  // ── Step 4: Import data ──
  if (step === 4) {
    // Sub-flow: user picked template or custom and is working through it
    if (importCard === 'template' || importCard === 'custom') {
      return (
        <div className="ob-overlay">
          <div className="ob-modal ob-modal--wide">
            {dots}
            <SpreadsheetImport
              onBack={() => setImportCard(null)}
              onDone={finishOnboarding}
            />
            <button className="ob-skip" onClick={finishOnboarding}>Skip import and continue</button>
          </div>
        </div>
      );
    }

    return (
      <div className="ob-overlay">
        <div className="ob-modal ob-modal--wide">
          {dots}
          <h2 className="ob-title">Get your dashboard working in minutes</h2>
          <p className="ob-text">
            Import your existing confidence scores now, or rate topics as you go. The more data you have, the sharper your priorities.
          </p>

          <div className="ob-import-cards">
            <button className="ob-import-card" onClick={() => setImportCard('template')}>
              <div className="ob-import-card-icon">📋</div>
              <div className="ob-import-card-title">Use our template</div>
              <div className="ob-import-card-text">Download a spreadsheet with all 259 topics pre-filled. Add your confidence scores, upload it back.</div>
              <div className="ob-import-card-cta">Download template →</div>
            </button>

            <button className="ob-import-card" onClick={() => setImportCard('custom')}>
              <div className="ob-import-card-icon">📊</div>
              <div className="ob-import-card-title">Import your spreadsheet</div>
              <div className="ob-import-card-text">Already tracking topics in your own spreadsheet? Upload it and we'll match your scores automatically.</div>
              <div className="ob-import-card-cta">Upload file →</div>
            </button>

            <button className="ob-import-card ob-import-card--skip" onClick={finishOnboarding}>
              <div className="ob-import-card-icon">✏️</div>
              <div className="ob-import-card-title">Rate topics as I go</div>
              <div className="ob-import-card-text">Start with a blank slate. Rate topics directly in the app as you revise.</div>
              <div className="ob-import-card-cta">Start now →</div>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
